import { uniq } from 'underscore';
import { mergeWith } from 'lodash';
import { normalize } from 'normalizr';
import { createReducer } from '@reduxjs/toolkit';

import { replaceArrays } from 'shared/lodash-utils';
import {
  MentoringProgramSchema,
  MentoringProgramsSchema,
} from 'redux/schemas/api/mentoring-programs';
import {
  cloneMentoringProgram,
  createMentoringProgram,
  deleteMentoringProgram,
  fetchProgramCounts,
  getMentoringProgram,
  getMentoringPrograms,
  notifyFirstConnectionAnnounced,
  setAutoMatchingLastUpdated,
  setAutoMatchingStatus,
  setConnectionsCount,
  setSaveStatus,
  setProgramCloningStatus,
  updateMentoringProgram,
  updateMentoringProgramHeader,
} from 'redux/actions/mentoring-programs';
import {
  createMentoringProgramRecruitment,
  deleteMentoringProgramRecruitment,
  updateMentoringProgramRecruitment,
} from 'redux/actions/mentoring-program-recruitments';
import { MentoringProgramNormalized } from 'redux/schemas/models/mentoring-programs';
import { initialRootState } from '.';

const setProgramActionHandler = (state, action) => {
  const normalized = normalize(action.payload, MentoringProgramSchema);

  mergeWith(state.models, normalized.entities, replaceArrays);
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(cloneMentoringProgram.fulfilled, (state, action) => {
      const sourceProgramId = action.meta.arg.id;
      const payload = action.payload || {};
      const { targetProgramId, mentorshipProgram, program } = payload;

      const programToNormalize = mentorshipProgram || program || (payload.id ? payload : undefined);
      const clonedId = targetProgramId || programToNormalize?.id;

      if (programToNormalize) {
        const normalized = normalize(programToNormalize, MentoringProgramSchema);
        mergeWith(state.models, normalized.entities, replaceArrays);
      }

      // Mark the program as "cloning in progress" so the dashboard subscribes
      // to Pusher. Prefer the target program ID from the API response; fall back
      // to the source program ID (always known) if the API returns no usable ID.
      const programIdToMark = clonedId || sourceProgramId;
      if (programIdToMark) {
        if (!state.models.mentoringPrograms[programIdToMark]) {
          const sourceProgram = state.models.mentoringPrograms[sourceProgramId] as MentoringProgramNormalized | undefined;
          state.models.mentoringPrograms[programIdToMark] = {
            id: programIdToMark,
            name: action.meta.arg.name,
            headerBackground: sourceProgram?.headerBackground,
          } as MentoringProgramNormalized;
        }
        (state.models.mentoringPrograms[programIdToMark] as MentoringProgramNormalized).isBeingCloned = true;
      }
    })
    .addCase(setProgramCloningStatus, (state, action) => {
      const { programId, isBeingCloned } = action.payload;
      if (state.models.mentoringPrograms[programId]) {
        (state.models.mentoringPrograms[programId] as MentoringProgramNormalized).isBeingCloned = isBeingCloned;
      }
    })
    .addCase(createMentoringProgram.fulfilled, setProgramActionHandler)
    .addCase(updateMentoringProgram.fulfilled, setProgramActionHandler)
    .addCase(updateMentoringProgramHeader.fulfilled, setProgramActionHandler)
    .addCase(getMentoringProgram.fulfilled, setProgramActionHandler)
    .addCase(getMentoringPrograms.fulfilled, (state, action) => {
      const normalized = normalize(
        action.payload.response,
        MentoringProgramsSchema,
      );

      mergeWith(state.models, normalized.entities, (objValue, srcValue, key) => {
        if (key === 'isBeingCloned' && objValue === true && srcValue === false) {
          return true;
        }
        return replaceArrays(objValue, srcValue);
      });
    })
    .addCase(fetchProgramCounts.pending, (state, action) => {
      if (action.meta.arg.setLoading ?? true) {
        state.app.mentoringProgram.programCounts.isLoading = true;
      }
    })
    .addCase(fetchProgramCounts.fulfilled, (state, action) => {
      state.app.mentoringProgram.programCounts.result = action.payload;
      state.app.mentoringProgram.programCounts.isLoading = false;
    })
    .addCase(fetchProgramCounts.rejected, state => {
      state.app.mentoringProgram.programCounts.isLoading = false;
    })
    .addCase(setAutoMatchingStatus, (state, action) => {
      const { autoMatchingStatus } = action.payload;
      state.app.mentoringProgram.autoMatching = {
        status: autoMatchingStatus,
      };
    })
    .addCase(setAutoMatchingLastUpdated, (state, action) => {
      const { mentorshipProgramId, autoMatchingLastUpdated } = action.payload;
      state.models.mentoringPrograms[
        mentorshipProgramId
      ].autoMatchingLastUpdated = autoMatchingLastUpdated;
    })
    .addCase(setConnectionsCount, (state, action) => {
      const { totalConnectionsCount } = action.payload;
      state.app.mentoringProgram.programCounts.result = {
        ...state.app.mentoringProgram.programCounts.result,
        connectionsCount: totalConnectionsCount,
      };
    })
    .addCase(setSaveStatus, (state, action) => {
      const { newStatus } = action.payload;
      state.app.mentoringProgram.settings.saveStatus = newStatus;
    })
    .addCase(deleteMentoringProgram.pending, (state, action) => {
      (state.models.mentoringPrograms[action.meta.arg] as MentoringProgramNormalized).isBeingDeleted = true;
    })
    .addCase(deleteMentoringProgram.fulfilled, (state, action) => {
      (state.models.mentoringPrograms[action.meta.arg] as MentoringProgramNormalized).isBeingDeleted = true;
    })
    .addCase(deleteMentoringProgram.rejected, (state, action) => {
      (state.models.mentoringPrograms[action.meta.arg] as MentoringProgramNormalized).isBeingDeleted = false;
    })
    .addCase(notifyFirstConnectionAnnounced.fulfilled, (state, action) => {
      state.models.mentoringProgramEnrollments[action.meta.arg.programEnrollmentId].firstConnectionAnnounced = true;
    })
    .addCase(createMentoringProgramRecruitment.fulfilled, (state, action) => {
      const { programId, role = 'mentor' } = action.meta.arg;
      const program = state.models.mentoringPrograms[programId] as MentoringProgramNormalized;
      if (program) {
        if (role === 'mentor') {
          program.mentorRecruitmentEnabled = true;
        }
        program.recruitments = { ...(program.recruitments || {}), [role]: action.payload };
      }
    })
    .addCase(updateMentoringProgramRecruitment.fulfilled, (state, action) => {
      const { programId, role } = action.meta.arg;
      const program = state.models.mentoringPrograms[programId] as MentoringProgramNormalized;
      if (program && role) {
        program.recruitments = { ...(program.recruitments || {}), [role]: action.payload };
      }
    })
    .addCase(deleteMentoringProgramRecruitment.fulfilled, (state, action) => {
      const { programId, role } = action.meta.arg;
      const program = state.models.mentoringPrograms[programId] as MentoringProgramNormalized;
      if (program && role) {
        if (role === 'mentor') {
          program.mentorRecruitmentEnabled = false;
        }
        const updatedRecruitments = { ...(program.recruitments || {}) };
        delete updatedRecruitments[role];
        program.recruitments = updatedRecruitments;
      }
    });
});
